import api from './axios';

export interface CarbonSummary {
  period: string;
  totalKgCo2e: number;
  companyId?: number;
  fallback?: boolean;
  enabled?: boolean;
}

export interface FactorSuggestion {
  matched: boolean;
  emissionFactor?: number;
  scope?: string;
  category?: string;
  rationale?: string;
  enabled?: boolean;
  error?: string;
  /** Why the proxy fell back: 'timeout' | 'unreachable' | 'saturated' | 'interrupted'. */
  reason?: string;
}

export interface SsoStartResponse {
  ticket: string;
  redirectUrl: string;
  expiresInSeconds: number;
}

export interface AiMessage { role: 'user' | 'assistant'; content: string; }

/**
 * Annual ESG / CSRD-light declaration. `financial` is local TuniFact data; `carbon`
 * wraps the GTS Carbon annual-report payload, whose inner `data` shape is owned by
 * GTS — the UI reads it defensively. `carbon.available` is false when the bridge is
 * disabled or GTS is unreachable.
 */
export interface EsgDeclaration {
  year: number;
  companyId: number;
  financial: { ca: number; nbFactures: number };
  carbon: {
    available: boolean;
    reason?: string;
    // GTS annual-report payload — totals + breakdowns by scope/category.
    data?: Record<string, any>;
  };
  disclaimer: string;
}

export const esgApi = {
  /** Annual ESG declaration for the current tenant. Gated on the ESG_DECLARATION plan feature. */
  annual: async (year: number): Promise<EsgDeclaration> => {
    const { data } = await api.get<EsgDeclaration>('/rapports/esg/annual', { params: { year } });
    return data;
  },
};

export const aiApi = {
  /** POST a chat turn to the GTS AI proxy. Returns whatever the upstream sends. */
  chat: async (messages: AiMessage[], context?: Record<string, unknown>): Promise<any> => {
    const { data } = await api.post('/ai/chat', { messages, context });
    return data;
  },
};

export const carbonApi = {
  /** Monthly carbon footprint for the current tenant. period = "YYYY-MM" or undefined. */
  summary: async (period?: string): Promise<CarbonSummary> => {
    const params = period ? { period } : {};
    const { data } = await api.get<CarbonSummary>('/carbon/summary', { params });
    return data;
  },

  /** Ask GTS Carbon to suggest an emission factor for a freeform article designation. */
  suggestFactor: async (designation: string, unite?: string): Promise<FactorSuggestion> => {
    const { data } = await api.get<FactorSuggestion>('/carbon/factors/suggest', {
      params: { designation, ...(unite ? { unite } : {}) },
    });
    return data;
  },

  /** Generate a short-lived SSO ticket and return the redirect URL into GTS Carbon. */
  startSso: async (returnPath?: string): Promise<SsoStartResponse> => {
    const { data } = await api.post<SsoStartResponse>('/sso/start', {
      app: 'gts-carbon',
      returnPath,
    });
    return data;
  },
};
